/**
 * Product Page Event Tracking
 *
 * Type-safe event tracking helpers for product detail page interactions.
 * Tracks user behavior like viewing products, selecting tiers, and adding to cart.
 */

import { trackEvent } from './eventTracker';

/**
 * Type-safe product page event tracking helpers
 */
export const trackProductEvent = {
    /**
     * Track when a product detail page is viewed
     */
    viewed: (productId: string, productName: string, categoryId?: string) =>
        trackEvent('product_viewed', {
            productId,
            productName,
            categoryId,
        }),

    /**
     * Track when a rental tier is selected
     */
    tierSelected: (
        productId: string,
        tierName: string,
        minDays: number,
        maxDays: number,
        price: number
    ) =>
        trackEvent('rental_tier_selected', {
            productId,
            tierName,
            minDays,
            maxDays,
            price,
        }),

    /**
     * Track when a subscription tier is selected
     */
    subscriptionSelected: (productId: string, months: number, monthlyRate: number) =>
        trackEvent('subscription_tier_selected', {
            productId,
            months,
            monthlyRate,
        }),

    /**
     * Track when user switches between rental and monthly rental modes
     */
    modeChanged: (
        productId: string,
        fromMode: 'short-term' | 'monthly-rental',
        toMode: 'short-term' | 'monthly-rental'
    ) =>
        trackEvent('rental_mode_changed', {
            productId,
            fromMode,
            toMode,
        }),

    /**
     * Track when a pickup date is selected
     */
    pickupDateSelected: (productId: string, date: string) =>
        trackEvent('pickup_date_selected', {
            productId,
            date,
        }),

    /**
     * Track when product quantity is changed
     */
    quantityChanged: (productId: string, oldQty: number, newQty: number) =>
        trackEvent('product_quantity_changed', {
            productId,
            oldQty,
            newQty,
        }),

    /**
     * Track add to cart button clicks
     */
    addToCartClicked: (productId: string, success: boolean, errorReason?: string) =>
        trackEvent('add_to_cart_clicked', {
            productId,
            success,
            errorReason,
        }),
};
